'use client';
import {usePathname, useRouter} from "next/navigation";
import React, {useState, useEffect, useRef} from 'react';
import {useAppDispatch, useAppSelector} from "@/app/storeHooks";
import {selectAccountInfo, updateAccountInfo} from "./accountSlice";
import {getAccountInfoData} from "./accountAPI";
import {useUser} from "@/app/context/UserContext";
import {Button, FontIcon, Input} from "@/app/_components";
import LoginNetManager from "@/Core/Login/LoginNetManager";
import {SDialogManagerFC} from "@/Master/SDialogFC/SDialogManagerFC";
import {PrivacyDialogFC} from "@/app/_dialogs/PrivacyDialog/PrivacyDialog";
import Load from "@/app/_components/Load/Load";
import UserMgr from "@/Core/UserMgr";
import {LoginUserType} from "@/Core/Login/types/user";
import {updateUser} from "@/app/_api/account";
import {InputRef} from "@/app/_components/Input/input";
import {Toast} from "@/app/_components";
import styles from "./page.module.css"
import {confJson} from "@/app/config/confJson";
import {AiHelpManager} from "@/Core/AIHelp/AiHelpBox";

const buttonStyles: React.CSSProperties = {
    marginBottom: ".2rem",
    height: "1.4rem",
    borderRadius: "1.2rem",
    fontSize: ".6rem",
    fontWeight: 600,
}

export default function AccountPage() {
    const router = useRouter();
    const pathname = usePathname();
    const user = useUser();
    const dispatch = useAppDispatch();
    const account = useAppSelector(selectAccountInfo);
    const nicknameRef = useRef<InputRef>(null);

    const [loading, setLoading] = useState(true);
    const [isEdit, setIsEdit] = useState(false);
    const [nickname, setNickname] = useState<string>(user.nickName);

    useEffect(() => {
        if (UserMgr.hasTokenOk(user.token)) {

            if (account.info.vipConfigList.vipLevels.length === 0) {
                getAccountInfoData(user).then(data => {
                    dispatch(updateAccountInfo(data))
                    setLoading(false);
                });
            } else {
                setLoading(false);
            }
        }
    }, [user.token]);

    const handleJump = (path: string) => {
        router.push(`${pathname}${path}`);
    }

    const handleOpenPrivacyPolicy = () => {
        SDialogManagerFC.openDialog(PrivacyDialogFC,{
            fcKey:SDialogManagerFC.getFCKey(PrivacyDialogFC),
            callAccept:()=>{}
        })
    }

    const handleSignOut = async () => {
        LoginNetManager.Instance.SendLogout((isOk:boolean)=>{
            if (isOk) {
                router.push("/login");
            }
        });
    }

    const handleUpdateUser = async () => {
        if (user.loginType == LoginUserType.visitor) {
            Toast.show({
                content: "Go to Login first",
                // position: 'top',
                afterClose:()=>{
                    console.log('afterClose');
                }
            })
            return;
        }
        setIsEdit(true);
        if (nicknameRef.current) {
            nicknameRef.current.focus();
        }
    }

    const handleNicknameSave = async () => {
        const response = await updateUser({userId: user.userId, nickname});
        if (response) {
            response && setIsEdit(false);
            UserMgr.updateUser('UpdateUserInfo', {
                nickName: nickname,
            });
        }
    }

    if (loading) return <Load/>;

    return (
        <div className={styles.page}>

            <div className={`${styles.box} ${styles.user_info_wrapper}`}>
                <div className={`${styles.service} btn`} onClick={()=>{
                    AiHelpManager.ShowContactUs();
                }}>
                    <FontIcon icon="s-kf" style={{fontSize: '.5rem'}}></FontIcon>
                </div>
                <div className={styles.avatar_wrapper}>
                    <div className={styles.avatar_box}>
                        <img className={styles.img_vip} src="/img/img_vip.webp" alt=""/>
                        <img className={styles.avatar} src="/img/img_tx1.webp" alt=""/>
                        <div className={styles.edit} onClick={handleUpdateUser}>
                            <FontIcon icon="s-pen" style={{fontSize: '.4rem'}}/>
                        </div>
                    </div>
                </div>
                {user.loginType == LoginUserType.visitor && <p className={styles.user_name}>userId: {user.userId}</p>}
                {user.loginType == LoginUserType.normal && <div className={styles.user_name}>
                    {!isEdit && <>{user.nickName}</>}
                    {isEdit && <div className={styles.edit_nickname_wrapper}>
                        <Input
                            ref={nicknameRef}
                            value={nickname}
                            type="text"
                            placeholder="Enter your nickname"
                            onChange={e => setNickname(e.target.value)}
                            extra={
                                <>
                                    <span className={styles.btn_save} onClick={handleNicknameSave}>save</span>
                                    <span className={styles.btn_cancel} onClick={() => setIsEdit(false)}>cancel</span>
                                </>
                            }
                        />
                    </div>}
                </div>}
                <div className={styles.title_box}>
                    <div className={styles.title_wrapper}>
                        <div className={styles.tips_icon} onClick={() => handleJump("/member")}>
                            <FontIcon
                                icon="s-ask"
                                style={{
                                    fontSize: '.4rem',
                                }}
                            />
                        </div>
                        {account.info.userVipName !== 'Hall of Fame' && <span
                            className={`${styles.title} ${styles['title_' + account.info.userVipName]}`}>{account.info.userVipName}</span>}
                        {account.info.userVipName === 'Hall of Fame' && <span
                            className={`${styles.title} ${styles.title_hallOfFame}`}>{account.info.userVipName}</span>}
                    </div>
                </div>
                <div className={styles.progress_wrapper} onClick={() => handleJump("/member")}>
                    <div
                        className={styles.progress}
                        style={{width: `${account.info.userVipExp/account.info.userNextVipExp}%`}}
                    >
                    </div>
                    <div className={styles.account_score}>{account.info.userVipExp}/{account.info.userNextVipExp}</div>
                    <div className={styles.mark}>
                        <img className={styles.mark_img} src={`/img/vip_icon_slices/${account.info.userVipName}.webp`} alt=""/>
                    </div>
                </div>
            </div>

            <div className={`${styles.box} ${styles.setting_wrapper}`}>
                {/*<div className={styles.sound_wrapper}>*/}
                {/*    <div>*/}
                {/*        <FontIcon item="#iconshengyin-yinliang" style={{fontSize: '.2rem'}}></FontIcon>*/}
                {/*        <span className={styles.sound_text}>Sound</span>*/}
                {/*    </div>*/}
                {/*    <div>*/}
                {/*        <Switch*/}
                {/*            style={{*/}
                {/*                // @ts-ignore*/}
                {/*                '--width': '1.5rem',*/}
                {/*                '--height': '.75rem',*/}
                {/*            }}*/}
                {/*        />*/}
                {/*    </div>*/}
                {/*</div>*/}

                <Button
                    style={buttonStyles}
                    color="#0090FF"
                    block
                >
                    My Profile
                </Button>
                <Button
                    style={buttonStyles}
                    color="#0090FF"
                    block
                    onClick={() => {
                        window.open(confJson.responsible_play_url);
                    }}
                >
                    Support
                </Button>
                <Button
                    style={buttonStyles}
                    color="#0090FF"
                    block
                    onClick={handleSignOut}
                >
                    Sign Out
                </Button>

                <div
                    className={styles.text_link}
                    onClick={handleOpenPrivacyPolicy}
                >
                    Privacy Policy
                </div>
            </div>

        </div>
    )
}
